/**
 * Analytics Overview Tab Component
 * Shows segment distribution, revenue by segment, top customers, top products
 */
import { PieChart, BarChart3, Crown, Zap, ChevronRight, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Segment icon mapping
const SEGMENT_ICONS = {
  champions: Crown,
  loyal_customers: Users,
  potential_loyalists: Users,
  new_customers: Users,
  promising: Users,
  needs_attention: Users,
  about_to_sleep: Users,
  at_risk: Users,
  hibernating: Users,
  lost: Users
};

export default function AnalyticsOverviewTab({ 
  segmentation, 
  productPerformance,
  onViewAllSegments,
  formatCurrency,
  formatPercent
}) {
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Segment Distribution Chart */}
        {segmentation && (
          <div className="bg-card rounded-xl p-6 border border-border">
            <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-primary" />
              Customer Segments
            </h3>
            <div className="space-y-3">
              {segmentation.segment_summaries?.slice(0, 6).map((seg) => {
                const Icon = SEGMENT_ICONS[seg.segment_id] || Users;
                return (
                  <div key={seg.segment_id} className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${seg.color}20` }}
                    >
                      <Icon className="w-4 h-4" style={{ color: seg.color }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-foreground">{seg.name}</span>
                        <span className="text-muted-foreground">
                          {seg.customer_count} ({formatPercent(seg.customer_percentage)})
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ 
                            width: `${seg.customer_percentage}%`,
                            backgroundColor: seg.color 
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4 gap-2"
              onClick={onViewAllSegments}
            >
              View All Segments <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Revenue by Segment */}
        {segmentation && (
          <div className="bg-card rounded-xl p-6 border border-border">
            <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Revenue by Segment
            </h3>
            <div className="space-y-3">
              {segmentation.segment_summaries
                ?.filter(s => s.total_revenue > 0)
                .sort((a, b) => b.total_revenue - a.total_revenue)
                .slice(0, 6)
                .map((seg) => (
                  <div key={seg.segment_id}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-foreground">{seg.name}</span>
                      <span className="font-medium" style={{ color: seg.color }}>
                        {formatCurrency(seg.total_revenue)} ({formatPercent(seg.revenue_percentage)})
                      </span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ 
                          width: `${seg.revenue_percentage}%`,
                          backgroundColor: seg.color 
                        }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Top Customers & Product Performance */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Customers */}
        {segmentation && (
          <div className="bg-card rounded-xl p-6 border border-border">
            <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-500" />
              Top Customers (Champions)
            </h3>
            <div className="space-y-3">
              {segmentation.customers
                ?.filter(c => c.segment === 'champions')
                .slice(0, 5)
                .map((customer, i) => (
                  <div key={customer.customer_id} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      i === 0 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                      i === 1 ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' :
                      i === 2 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground truncate">
                        {customer.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {customer.frequency} orders • Last {customer.recency_days}d ago
                      </p>
                    </div>
                    <span className="text-sm font-medium text-primary">
                      {formatCurrency(customer.monetary)}
                    </span>
                  </div>
                ))}
              {(!segmentation.customers?.filter(c => c.segment === 'champions').length) && (
                <p className="text-center text-muted-foreground py-4">
                  No champion customers yet
                </p>
              )}
            </div>
          </div>
        )}

        {/* Most Ordered */}
        {productPerformance && (
          <div className="bg-card rounded-xl p-6 border border-border">
            <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              Most Ordered Products
            </h3>
            <div className="space-y-3">
              {productPerformance.best_sellers?.slice(0, 5).map((item, i) => (
                <div key={item.item_id} className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    i === 0 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                    i === 1 ? 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {i + 1}
                  </span>
                  {item.image ? (
                    <img src={item.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-muted" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-foreground truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.units_sold} sold</p>
                  </div>
                  <span className="text-sm font-medium text-primary">
                    {formatCurrency(item.revenue)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
